import React from "react"
import Button from "@material-ui/core/Button"
import CircularProgress from "@material-ui/core/CircularProgress"
import Checkbox from "@material-ui/core/Checkbox"
import clsx from "clsx"
import Checkmark from "../../../components/Checkmark"
import InputLabel from "@material-ui/core/FormLabel"
import FormControlLabel from "@material-ui/core/FormControlLabel"
import TextField from "@material-ui/core/TextField"
import Typography from "@material-ui/core/Typography"
import { StyledAccordion } from "../StyledAccordion"
import AbilitiesTable from "../../../components/AbilitiesTable"
import AssociationsManager from "../AssociationsManager"
import MemberMenu from "../MemberMenu"
import Stack from "../../../components/Stack"
import { MembershipRoleEnum } from "../../../types/memberships"
import { useTranslations } from "./use-translations"
import { Plus } from "react-feather"
import { Theme, alpha, makeStyles, useMediaQuery, useTheme } from "@material-ui/core"
import { useNewMemberAbilities } from "./use-new-member-abilities"
import { useOptions } from "../../../options/hooks/use-options"
import { truncate } from "lodash"
import { IOptionNormalized } from "../../../options/interfaces/options-normalized"
import { useAuthUserMembership } from "../../hooks/use-auth-user-membership"
import InvitationEmailLanguageSwitch from "../InvitationEmailLanguageSwitch"
import { useMembershipFormData } from "./use-membership-form-data"
import { BillingAlert } from "../BillingAlert"

type UseMembershipFormReturnType = ReturnType<typeof useMembershipFormData>

const getCostEstimationScenarios = (formState: UseMembershipFormReturnType) => {
  if (!formState.values.sendInvitation) {
    return []
  }

  if (formState.isCopiedMember) {
    return [
      {
        type: "addMemberClone",
        sourceMemberId: formState.values.copyPermissionsFrom!,
        count: 1,
      } as const,
    ]
  }

  return [
    {
      type: "addMemberRole",
      role: formState.isLimitedMember ? MembershipRoleEnum.orgLimitedMember : MembershipRoleEnum.orgMember,
      count: 1,
      roleAbilityOverrides: {
        add: formState.values.abilitiesToAdd,
        remove: formState.values.abilitiesToRemove,
      },
    } as const,
  ]
}

export const useMembershipForm = ({ orgId }: { orgId: string }) => {
  const { customerOptions, membershipOptions, supplierOptions } = useOptions(orgId)
  const [open, setOpen] = React.useState(false)

  const membershipFormData = useMembershipFormData({ orgId })

  const copiedMember = getMemberById(membershipOptions, membershipFormData.values.copyPermissionsFrom)
  const translations = useTranslations({
    copyingMemberName: copiedMember?.name || "",
    copyToName: membershipFormData.values.firstname,
    copyFromName: copiedMember?.name || "",
    accessLevelMember: membershipFormData.values.firstname || membershipFormData.values.userEmail || undefined,
    invitationEmail: membershipFormData.values.userEmail || "",
  })
  const copyFromMemberLabel = membershipFormData.isCopiedMember
    ? truncate(translations.copyingMemberLabel, { length: 20 })
    : translations.copyFromMemberLabel

  const helperText = membershipFormData.isCopiedMember
    ? translations.copiedMemberHelperText
    : membershipFormData.isStandardMember
    ? translations.normalMembershipHelperText
    : membershipFormData.isLimitedMember
    ? translations.limitedMembershipHelperText
    : ""

  return {
    ...membershipFormData,
    customerOptions,
    membershipOptions,
    supplierOptions,
    copyFromMemberLabel,
    helperText,
    setOpen,
    open,
    translations,
    costEstimationScenarios: getCostEstimationScenarios(membershipFormData),
  }
}

const MembershipForm = ({ orgId }: { orgId: string }) => {
  const classes = useStyles()
  const isSmAndUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"))
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const { canCreateMemberships } = useAuthUserMembership(orgId)

  const {
    errors,
    values,
    handleChange,
    handleChangeEmail,
    handleChangeSendInvitation,
    handleChangeLanguage,
    handleSubmit,
    setAbilitiesInResources,
    setAssociations,
    setCopyPermissionsFrom,
    updateAbilities,
    isLimitedMember,
    isPermissionManagerVisible,
    isStandardMember,
    isCopiedMember,
    isFirstnameError,
    isSubmitting,
    isUserEmailError,
    customerOptions,
    membershipOptions,
    supplierOptions,
    copyFromMemberLabel,
    helperText,
    translations,
    costEstimationScenarios,
  } = useMembershipForm({ orgId })
  const { groupedAbilities } = useNewMemberAbilities({
    orgId,
    isLimitedMember: isLimitedMember,
    isStandardMember: isStandardMember,
    abilitiesToAdd: values.abilitiesToAdd,
    abilitiesToRemove: values.abilitiesToRemove,
  })
  const checkmarkIcon = <Checkmark variant="contained" size={20} />
  const theme = useTheme()

  if (!canCreateMemberships) {
    return null
  }

  return (
    <>
      <Typography variant="h3" className={classes.pageHeader}>
        {translations.pageHeader}
      </Typography>
      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <div>
            <InputLabel error={isFirstnameError} htmlFor="firstname">
              {translations.firstnameLabel}
            </InputLabel>
            <TextField
              id="firstname"
              name="firstname"
              error={isFirstnameError}
              helperText={isFirstnameError ? errors.firstname : undefined}
              value={values.firstname}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              inputProps={{ "data-test": "member-firstname-input" }}
            />
          </div>
          <div>
            <InputLabel htmlFor="lastname">{translations.lastnameLabel}</InputLabel>
            <TextField
              id="lastname"
              name="lastname"
              value={values.lastname}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              inputProps={{ "data-test": "member-lastname-input" }}
            />
          </div>
          <div>
            <InputLabel error={isUserEmailError} htmlFor="userEmail">
              {translations.emailLabel}
            </InputLabel>
            <TextField
              id="userEmail"
              name="userEmail"
              error={isUserEmailError}
              helperText={isUserEmailError ? errors.userEmail : undefined}
              value={values.userEmail}
              onChange={handleChangeEmail}
              variant="outlined"
              fullWidth
              inputProps={{ "data-test": "member-email-input" }}
            />
          </div>

          <FormControlLabel
            label={translations.sendInvitationLabel}
            control={
              <Checkbox
                checked={values.sendInvitation}
                onChange={(e) => {
                  handleChangeSendInvitation(e)
                }}
                name="sendInvitation"
                color="primary"
                size="small"
                data-test="send-invitation-checkbox"
              />
            }
          />

          {values.sendInvitation ? (
            <InvitationEmailLanguageSwitch
              language={values.language}
              onChangeLanguage={handleChangeLanguage}
              shouldShowLanguageSwitch={values.sendInvitation}
              translations={translations}
            />
          ) : null}

          {isPermissionManagerVisible && (
            <div style={{ backgroundColor: alpha(theme.palette.primary.main, 0.05), padding: 16, borderRadius: 4 }}>
              <Typography variant="body2" gutterBottom>
                {translations.accessLevelInfo}
              </Typography>
              <StyledAccordion title={translations.customizeAccess}>
                <>
                  <Stack direction={isSmAndUp ? "row" : "column"} spacing={1}>
                    <Button
                      color="primary"
                      variant="outlined"
                      className={clsx(classes.button, isStandardMember && "selected")}
                      onClick={() => setCopyPermissionsFrom(MembershipRoleEnum.orgMember)}
                      startIcon={isStandardMember ? checkmarkIcon : null}
                      fullWidth
                      data-test="standard-member-button"
                    >
                      {translations.standardMemberLabel}
                    </Button>
                    <Button
                      color="primary"
                      variant="outlined"
                      className={clsx(classes.button, isLimitedMember && "selected")}
                      onClick={() => setCopyPermissionsFrom(MembershipRoleEnum.orgLimitedMember)}
                      startIcon={isLimitedMember ? checkmarkIcon : null}
                      fullWidth
                      data-test="limited-member-button"
                    >
                      {translations.limitedMemberLabel}
                    </Button>
                    <Button
                      className={clsx(classes.button, isCopiedMember && "selected")}
                      color="primary"
                      variant="outlined"
                      onClick={(e) => {
                        setAnchorEl(e.currentTarget)
                        setCopyPermissionsFrom()
                      }}
                      startIcon={isCopiedMember ? checkmarkIcon : null}
                      fullWidth
                      data-test="copied-member-button"
                    >
                      {copyFromMemberLabel}
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="textSecondary" style={{ margin: "12px 0 12px 0" }} component="p">
                    {helperText}
                  </Typography>
                  <MemberMenu
                    anchor={anchorEl}
                    menuTitle={translations.chooseMemberToCopyLabel}
                    searchInputPlaceholder={translations.searchMembersPlaceholder}
                    options={getMembersWithAccess(membershipOptions)}
                    selected={values.copyPermissionsFrom}
                    onClickAway={() => {
                      // should fallback to orgMember if no member is selected
                      if (!values.copyPermissionsFrom && anchorEl) {
                        setCopyPermissionsFrom(MembershipRoleEnum.orgMember)
                      }
                      setAnchorEl(null)
                    }}
                    onClickMenuItem={({ id }) => {
                      setCopyPermissionsFrom(id)
                      setAnchorEl(null)
                    }}
                  />
                  {/* The association customization section is visible only when the new member's type is limited member */}
                  {isLimitedMember && (
                    <StyledAccordion title={translations.customizeAssociationsLabel}>
                      <AssociationsManager
                        abilities={values.abilitiesToAdd}
                        abilitiesInResources={values.abilitiesInResources}
                        associations={values.associations}
                        setAbilitiesInResources={setAbilitiesInResources}
                        setAssociations={setAssociations}
                        updateAbilities={updateAbilities}
                        customerOptions={customerOptions}
                        supplierOptions={supplierOptions}
                        canUpdatePermissions={true}
                      />
                    </StyledAccordion>
                  )}
                  {/* The permissions customization section is visible only when the new member's type is limited member  or standard member*/}
                  {(isLimitedMember || isStandardMember) && (
                    <StyledAccordion title={translations.customizePermissionsLabel}>
                      <AbilitiesTable groupedAbilities={groupedAbilities} onUpdateAbilities={updateAbilities} />
                    </StyledAccordion>
                  )}
                </>
              </StyledAccordion>
            </div>
          )}
          {values.sendInvitation ? (
            <BillingAlert orgId={orgId} translations={translations} costEstimationScenarios={costEstimationScenarios} />
          ) : null}
          <Stack spacing={1}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
              endIcon={isSubmitting ? null : <Plus size={16} />}
              disabled={isSubmitting}
              data-test="add-member-button"
            >
              {isSubmitting ? translations.addingMemberButtonLabel : translations.addMemberButtonLabel}
            </Button>
            {values.sendInvitation && values.userEmail ? (
              <Typography variant="caption" color="textSecondary">
                {translations.invitationInfo}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </form>
    </>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  pageHeader: { marginBottom: theme.spacing(4) },
  button: {
    fontWeight: theme.typography.fontWeightMedium as any,
    letterSpacing: theme.typography.body1.letterSpacing,
    fontSize: theme.typography.body2.fontSize,
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
    borderColor: theme.palette.divider,
    "&.selected": {
      outline: `1px solid ${theme.palette.primary.main}`,
      background: alpha(theme.palette.primary.main, 0.075),
      borderColor: theme.palette.primary.main,
      "& .MuiButton-startIcon": {
        paddingRight: theme.spacing(),
      },
    },
  },
}))

const getMemberById = (options: IOptionNormalized[], id?: string) => {
  return options.find((member) => member.id === id)
}

const getMembersWithAccess = (options: IOptionNormalized[]) => {
  const filteredOptions = options.filter((option) => option.type !== "noAccessMember")
  return filteredOptions.sort((a, b) => a.name.localeCompare(b.name))
}

export default MembershipForm
